import React, { useMemo } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import { useTheme } from '../../contexts/ThemeContext';

interface TutorialTourProps {
  run: boolean;
  onComplete: () => void;
}

export const TutorialTour: React.FC<TutorialTourProps> = ({ run, onComplete }) => {
  const { darkMode } = useTheme();

  const steps: Step[] = useMemo(() => [
    {
      target: '[data-tour="dashboard-overview"]',
      content: 'Welcome to TallyUp! This is your financial dashboard where you can see a snapshot of your entire financial picture at a glance.',
      title: 'Your Dashboard',
      placement: 'auto',
      disableBeacon: true,
    },
    {
      target: '[data-tour="sidebar-nav"]',
      content: 'Use the sidebar to navigate between sections: Accounts, Investments, Charts, Assets & Debts, and Budget & Goals.',
      title: 'Navigation',
      placement: 'auto',
    },
    {
      target: '[data-tour="accounts-card"]',
      content: 'Click into Accounts to manage your bank accounts and credit cards. Use the + button to add new accounts, and click any balance to edit it inline.',
      title: 'Add & Edit Accounts',
      placement: 'auto',
    },
    {
      target: '[data-tour="net-worth-display"]',
      content: 'Your total net worth is always visible here. It updates automatically as you edit your financial data.',
      title: 'Net Worth',
      placement: 'auto',
    },
    {
      target: '[data-tour="refresh-button"]',
      content: 'Hit refresh to sync the latest data from your Google Sheet and update live market prices.',
      title: 'Refresh Data',
      placement: 'auto',
    },
    {
      target: '[data-tour="privacy-toggle"]',
      content: 'Toggle privacy mode to hide sensitive numbers. Great for when you\'re sharing your screen.',
      title: 'Privacy Mode',
      placement: 'auto',
    },
    {
      target: '[data-tour="settings-button"]',
      content: 'Access your account settings, appearance, and support options here.',
      title: 'Settings',
      placement: 'auto',
    },
  ], []);

  const handleCallback = (data: CallBackProps) => {
    const { status, action, type } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      onComplete();
    }
    // Also mark complete when user closes a tooltip mid-tour (X button or overlay click)
    if (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER) {
      onComplete();
    }
  };

  const bgColor = darkMode ? '#1f2937' : '#ffffff';
  const textColor = darkMode ? '#e5e7eb' : '#374151';
  const primaryColor = '#8b5cf6';

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      scrollOffset={80}
      disableScrollParentFix
      callback={handleCallback}
      styles={{
        options: {
          arrowColor: bgColor,
          backgroundColor: bgColor,
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          primaryColor,
          textColor,
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 12,
          padding: 20,
        },
        tooltipTitle: {
          fontSize: 16,
          fontWeight: 700,
          color: darkMode ? '#ffffff' : '#111827',
        },
        tooltipContent: {
          fontSize: 14,
          lineHeight: 1.5,
        },
        buttonNext: {
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          padding: '8px 16px',
        },
        buttonBack: {
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          color: textColor,
        },
        buttonSkip: {
          borderRadius: 8,
          fontSize: 13,
          color: darkMode ? '#6b7280' : '#9ca3af',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Done',
        next: 'Next',
        skip: 'Skip Tour',
      }}
    />
  );
};
